<script setup lang="ts">
import { computed, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import PropertySheetHost from './PropertySheetHost.vue'
import PropertySortPanel from './PropertySortPanel.vue'
import type { SortByOption } from '../types/propertyTypes'

// Sort trigger + sheet. The choice is a draft until "Apply Sort"; closing
// the sheet any other way discards it.

interface Props {
  options: SortByOption[]
  modelValue: string
}

const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>()

const hostRef = ref<InstanceType<typeof PropertySheetHost> | null>(null)
const draft = ref('')

const activeLabel = computed(() => {
  const match = props.options.find((option) => option.value === props.modelValue)
  return match ? `Sort: ${match.label}` : 'Sort'
})

// Opening starts a fresh draft from the applied sort; tapping again closes.
const handleTriggerClick = (isOpen: boolean) => {
  if (isOpen) {
    hostRef.value?.close()
    return
  }
  draft.value = props.modelValue
  hostRef.value?.open()
}

const apply = () => {
  emit('update:modelValue', draft.value || props.options[0]?.value || '')
  hostRef.value?.close()
}
</script>

<template>
  <PropertySheetHost ref="hostRef">
    <template #trigger="{ isOpen }">
      <Box
        is="button"
        type="button"
        align="center"
        justify="center"
        class="sort-menu__trigger"
        :class="{ 'sort-menu__trigger--active': Boolean(modelValue) || isOpen }"
        :aria-label="activeLabel"
        :title="activeLabel"
        aria-haspopup="dialog"
        :aria-expanded="isOpen"
        @click="handleTriggerClick(isOpen)"
      >
        <PiIcon name="sort-amount-down" class="sort-menu__icon" />
      </Box>
    </template>
    <template #default="{ isSheet }">
      <PropertySortPanel
        v-model="draft"
        :options="options"
        :is-sheet="isSheet"
        @apply="apply"
        @close="hostRef?.close()"
      />
    </template>
  </PropertySheetHost>
</template>

<style scoped>
.sort-menu__trigger {
  width: 38px;
  height: 38px;
  padding: 0;
  flex-shrink: 0;
  border-radius: 10px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  cursor: pointer;
  transition: border-color 0.12s;
}

.sort-menu__trigger:hover {
  border-color: var(--p-primary-300);
}

.sort-menu__trigger--active {
  background: var(--p-primary-50);
  border-color: var(--p-primary-500);
}

.sort-menu__trigger:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.sort-menu__icon {
  font-size: 16px;
  color: var(--p-text-color);
}

.sort-menu__trigger--active .sort-menu__icon {
  color: var(--p-primary-600);
}
</style>
